<template>
  <Teleport to="body">
    <Transition name="phone-code-popover">
      <div
        v-if="isOpen"
        class="phone-code-popover"
        :style="popoverStyle"
        role="listbox"
        @click.stop
      >
        <div class="phone-code-popover-search">
          <svg
            class="phone-code-popover-search-icon"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="11"
              cy="11"
              r="8"
              stroke="currentColor"
              stroke-width="2"
            />
            <path
              d="m21 21-4.35-4.35"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
          <input
            ref="searchInputRef"
            :value="searchQuery"
            type="text"
            :placeholder="t('phone.codeDialog.searchPlaceholder')"
            class="phone-code-popover-search-input"
            @input="handleSearchUpdate"
            @keydown.escape="handleClose"
          />
          <button
            v-if="searchQuery"
            type="button"
            class="phone-code-popover-search-clear"
            @click="handleClearSearch"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="m15 9-6 6M9 9l6 6"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <div class="phone-code-popover-list">
          <div
            v-for="(item, i) in filteredPhoneCodes"
            :key="i"
            class="phone-code-popover-item"
            @click="handleSelect(item)"
          >
            <FlagIcon
              :iso-code="item.isoCode"
              :width="28"
              :border-radius="4"
              class="phone-code-popover-item-flag"
            />
            <div class="phone-code-popover-item-content">
              <span class="phone-code-popover-item-name">{{ getDisplayName(item) }}</span>
              <span class="phone-code-popover-item-code">+{{ item.phoneCode }}</span>
            </div>
          </div>
          <div
            v-if="hasNoResults"
            class="phone-code-popover-item phone-code-popover-item-empty"
          >
            {{ t('phone.codeDialog.empty') }}
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, watch, nextTick, ref } from 'vue'
import type { PhoneCode } from '@/constants/phone-codes'
import FlagIcon from './FlagIcon.vue'
import { usePhoneCodeLocalization, usePhoneCodeFilter } from '@/composables/phone'

interface Props {
  modelValue: boolean
  searchQuery?: string
  /** Стили позиционирования от useDropdown (position, top/bottom, left, maxHeight) */
  dropdownStyle?: Record<string, string>
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'update:searchQuery', value: string): void
  (e: 'select', value: PhoneCode): void
}

const props = withDefaults(defineProps<Props>(), {
  searchQuery: '',
  dropdownStyle: () => ({}),
})

const emit = defineEmits<Emits>()

const { t } = useI18n()
const { getDisplayName } = usePhoneCodeLocalization()

const searchQueryRef = computed(() => props.searchQuery)
const { filteredPhoneCodes, hasNoResults } = usePhoneCodeFilter(searchQueryRef)

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const popoverStyle = computed(() => ({
  ...props.dropdownStyle,
  width: props.dropdownStyle?.width ?? '320px',
  minWidth: '280px',
}))

const searchInputRef = ref<HTMLInputElement | null>(null)

watch(isOpen, (open) => {
  if (open) {
    nextTick(() => searchInputRef.value?.focus({ preventScroll: true }))
  }
})

const handleSearchUpdate = (event: Event) => {
  const target = event.target as HTMLInputElement
  emit('update:searchQuery', target.value)
}

const handleClearSearch = () => {
  emit('update:searchQuery', '')
}

const handleClose = () => {
  emit('update:searchQuery', '')
  isOpen.value = false
}

const handleSelect = (country: PhoneCode) => {
  emit('select', country)
  emit('update:searchQuery', '')
  isOpen.value = false
}
</script>

<style scoped>
.phone-code-popover {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* border: 1px solid var(--color-grey-light, #E5E7EB); */
}

.phone-code-popover-enter-active,
.phone-code-popover-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.phone-code-popover-enter-from,
.phone-code-popover-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

.phone-code-popover-search {
  position: relative;
  display: flex;
  align-items: center;
  border-bottom: 1px solid var(--color-grey-light, #E5E7EB);
  padding: 10px 12px;
  flex-shrink: 0;
}

.phone-code-popover-search-icon {
  color: var(--color-grey, #868D98);
  margin-right: 8px;
  flex-shrink: 0;
}

.phone-code-popover-search-input {
  flex: 1;
  border: none;
  outline: none;
  font-family: var(--font-inter);
  font-size: 14px;
  color: var(--color-dark-blue, #252840);
  min-height: 36px;
}

.phone-code-popover-search-input::placeholder {
  color: var(--color-grey, #868D98);
}

.phone-code-popover-search-input:focus {
  outline: none;
}

.phone-code-popover-search-clear {
  background: none;
  border: none;
  color: var(--color-grey, #868D98);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
}

.phone-code-popover-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

.phone-code-popover-list::-webkit-scrollbar {
  width: 6px;
}

.phone-code-popover-list::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 3px;
}

.phone-code-popover-list::-webkit-scrollbar-thumb {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 3px;
}

.phone-code-popover-item {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  min-height: 48px;
}

.phone-code-popover-item:hover {
  background-color: rgba(0, 0, 0, 0.04);
}

.phone-code-popover-item-empty {
  justify-content: center;
  color: var(--color-grey, #868D98);
  cursor: default;
}

.phone-code-popover-item-empty:hover {
  background-color: transparent;
}

.phone-code-popover-item-flag {
  margin-right: 12px;
  flex-shrink: 0;
}

.phone-code-popover-item-content {
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  gap: 8px;
}

.phone-code-popover-item-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-family: var(--font-inter);
  color: var(--color-dark-blue, #252840);
}

.phone-code-popover-item-code {
  font-weight: 500;
  flex-shrink: 0;
  white-space: nowrap;
  font-size: 14px;
  font-family: var(--font-inter);
  color: var(--color-dark-blue, #252840);
}
</style>
